import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Fab,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Badge,
  Divider,
  Collapse,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, ListAlt, Warning, ExpandMore, ExpandLess } from '@mui/icons-material';
import useStyles from './style';
import { MESSAGE } from '../../../constants/message';

const NAVIGATOR_SCROLL_OFFSET = 120;
const NAVIGATOR_HIGHLIGHT_DELAY = 180;
const NAVIGATOR_INTERNAL_SCROLL_BUFFER = 72;
const SUBLOCATION_ACTIVE_HYSTERESIS = 0.08;
const ASSET_ACTIVE_RATIO_MIN = 0.12;
const ASSET_ACTIVE_HYSTERESIS = 0.1;
const getAssetNavKey = (sublocationId, assetId) => `${sublocationId}-${assetId}`;
const getSublocationIdFromAssetNavKey = (assetNavKey = '') => assetNavKey.split('-')[0];

const SublocationNavigator = ({
  sublocations = [],
  sublocationWarnings = {},
  assetWarnings = {},
  isLoading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSublocationId, setActiveSublocationId] = useState(null);
  const [activeAssetKey, setActiveAssetKey] = useState(null);
  const [expandedSublocations, setExpandedSublocations] = useState({});
  const [queuedNavigation, setQueuedNavigation] = useState(null);
  const classes = useStyles();
  const open = Boolean(anchorEl);
  const observerRef = useRef(null);
  const popoverPaperRef = useRef(null);
  const popoverActionRef = useRef(null);
  const sublocationItemRefs = useRef({});
  const assetItemRefs = useRef({});
  const activeSublocationIdRef = useRef(null);
  const activeAssetKeyRef = useRef(null);

  const hasAnyWarning =
    Object.values(sublocationWarnings).some(Boolean) ||
    Object.values(assetWarnings).some((sublocationAssetWarnings) =>
      Object.values(sublocationAssetWarnings || {}).some(Boolean),
    );

  // When loading finishes, execute any queued navigation
  useEffect(() => {
    if (!isLoading && queuedNavigation) {
      const { type, sublocationId, assetId } = queuedNavigation;
      if (type === 'asset') {
        scrollToAsset(sublocationId, assetId);
      } else if (type === 'summary') {
        scrollToSummary(sublocationId);
      }
      setQueuedNavigation(null);
    }
  }, [isLoading, queuedNavigation]);

  useEffect(() => {
    if (!sublocations.length) {
      return;
    }

    setExpandedSublocations((prev) => {
      const next = { ...prev };
      let hasChange = false;

      sublocations.forEach((sublocation) => {
        if (next?.[sublocation.id] == null) {
          next[sublocation.id] = true;
          hasChange = true;
        }
      });

      return hasChange ? next : prev;
    });
  }, [sublocations]);

  useEffect(() => {
    activeSublocationIdRef.current = activeSublocationId;
  }, [activeSublocationId]);

  useEffect(() => {
    activeAssetKeyRef.current = activeAssetKey;
  }, [activeAssetKey]);

  useEffect(() => {
    if (!sublocations.length || typeof IntersectionObserver === 'undefined') return;

    const visibleEntries = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibleEntries.set(entry.target.id, entry.intersectionRatio);
        });

        let bestSublocationId = null;
        let bestSublocationRatio = 0;
        let bestAssetId = null;
        let bestAssetRatio = 0;

        visibleEntries.forEach((ratio, id) => {
          if (id.startsWith('sublocation-') && ratio > bestSublocationRatio) {
            bestSublocationRatio = ratio;
            bestSublocationId = id;
          }

          if (id.startsWith('asset-') && ratio > bestAssetRatio) {
            bestAssetRatio = ratio;
            bestAssetId = id;
          }
        });

        const nextActiveSublocationId = bestSublocationId ? bestSublocationId.replace('sublocation-', '') : null;
        const nextActiveAssetKey = bestAssetId ? bestAssetId.replace('asset-', '') : null;
        const nextActiveAssetSublocationId = nextActiveAssetKey
          ? getSublocationIdFromAssetNavKey(nextActiveAssetKey)
          : null;
        const currentActiveSublocationId = activeSublocationIdRef.current;
        const currentActiveAssetKey = activeAssetKeyRef.current;
        const currentSublocationRatio = currentActiveSublocationId
          ? visibleEntries.get(`sublocation-${currentActiveSublocationId}`) ?? 0
          : 0;
        const currentActiveAssetSublocationId = currentActiveAssetKey
          ? getSublocationIdFromAssetNavKey(currentActiveAssetKey)
          : null;
        const currentAssetRatio = currentActiveAssetKey ? visibleEntries.get(`asset-${currentActiveAssetKey}`) ?? 0 : 0;

        const resolvedActiveSublocationId =
          currentActiveSublocationId &&
          currentSublocationRatio > 0 &&
          currentSublocationRatio >= bestSublocationRatio - SUBLOCATION_ACTIVE_HYSTERESIS
            ? currentActiveSublocationId
            : nextActiveSublocationId;

        const resolvedActiveAssetKey =
          nextActiveAssetSublocationId &&
          String(nextActiveAssetSublocationId) === String(resolvedActiveSublocationId) &&
          bestAssetRatio >= ASSET_ACTIVE_RATIO_MIN
            ? currentActiveAssetKey &&
              String(currentActiveAssetSublocationId) === String(resolvedActiveSublocationId) &&
              currentAssetRatio >= ASSET_ACTIVE_RATIO_MIN / 2 &&
              currentAssetRatio >= bestAssetRatio - ASSET_ACTIVE_HYSTERESIS
              ? currentActiveAssetKey
              : nextActiveAssetKey
            : null;

        if (activeSublocationIdRef.current !== resolvedActiveSublocationId) {
          setActiveSublocationId(resolvedActiveSublocationId);
        }

        if (activeAssetKeyRef.current !== resolvedActiveAssetKey) {
          setActiveAssetKey(resolvedActiveAssetKey);
        }
      },
      { threshold: [0, 0.01, 0.05, 0.1, 0.25, 0.5] },
    );

    observerRef.current = observer;

    const observeElements = () => {
      sublocations.forEach((sub) => {
        const sublocationElement = document.getElementById(`sublocation-${sub.id}`);
        if (sublocationElement) observer.observe(sublocationElement);

        sub?.assets?.forEach((asset) => {
          const assetElement = document.getElementById(`asset-${sub.id}-${asset.id}`);
          if (assetElement) observer.observe(assetElement);
        });
      });
    };

    // Elements may not be in the DOM yet — retry until they appear
    observeElements();
    const interval = setInterval(() => {
      const found = sublocations.some((sub) => document.getElementById(`sublocation-${sub.id}`));
      if (found) {
        observer.disconnect();
        visibleEntries.clear();
        observeElements();
        clearInterval(interval);
      }
    }, 200);

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [sublocations]);

  const handleClick = (event) => {
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBlur = (event) => {
    if (!open) {
      return;
    }

    const nextFocusedElement = event.relatedTarget;

    if (
      nextFocusedElement &&
      (event.currentTarget.contains(nextFocusedElement) || popoverPaperRef.current?.contains(nextFocusedElement))
    ) {
      return;
    }

    handleClose();
  };

  const stopIndicatorInteraction = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const toggleAssets = (sublocationId, event) => {
    event.stopPropagation();
    setExpandedSublocations((prev) => ({
      ...prev,
      [sublocationId]: !prev?.[sublocationId],
    }));
  };

  const handleNavigatorWheel = (event) => {
    const container = popoverPaperRef.current;

    if (!container || container.scrollHeight <= container.clientHeight) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const nextScrollTop = Math.min(Math.max(container.scrollTop + event.deltaY, 0), maxScrollTop);

    container.scrollTop = nextScrollTop;
  };

  const updatePopoverPosition = () => {
    popoverActionRef.current?.updatePosition?.();
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      updatePopoverPosition();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [open, expandedSublocations]);

  const scrollNavigatorItemIntoView = (element) => {
    const container = popoverPaperRef.current;

    if (!container || !element) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const topActionHeight = container.querySelector('[data-testid="sublocation-nav-top"]')?.offsetHeight ?? 0;
    const bottomActionHeight = container.querySelector('[data-testid="sublocation-nav-bottom"]')?.offsetHeight ?? 0;
    const visibleTop = containerRect.top + topActionHeight + NAVIGATOR_INTERNAL_SCROLL_BUFFER;
    const visibleBottom = containerRect.bottom - bottomActionHeight - NAVIGATOR_INTERNAL_SCROLL_BUFFER;
    const scrollPadding = 8;

    if (elementRect.top < visibleTop) {
      container.scrollTop += elementRect.top - visibleTop - scrollPadding;
      return;
    }

    if (elementRect.bottom > visibleBottom) {
      container.scrollTop += elementRect.bottom - visibleBottom + scrollPadding;
    }
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const activeAssetSublocationId = activeAssetKey ? getSublocationIdFromAssetNavKey(activeAssetKey) : null;
    const shouldTrackExpandedAsset =
      activeAssetSublocationId &&
      String(activeAssetSublocationId) === String(activeSublocationId) &&
      expandedSublocations[activeAssetSublocationId];
    const activeElement = shouldTrackExpandedAsset
      ? assetItemRefs.current[activeAssetKey]
      : sublocationItemRefs.current[activeSublocationId];

    scrollNavigatorItemIntoView(activeElement);
  }, [open, activeSublocationId, activeAssetKey, expandedSublocations]);

  const highlightScrolledElement = (element) => {
    const highlightTarget = element?.id?.startsWith('sublocation-')
      ? element.querySelector('.MuiAccordionSummary-root') || element.querySelector('.MuiAccordion-root') || element
      : element;

    if (typeof highlightTarget?.animate === 'function') {
      highlightTarget.animate(
        [
          {
            backgroundColor: 'rgba(25, 118, 210, 0.18)',
            boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.24)',
          },
          {
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.14)',
          },
          {
            backgroundColor: 'transparent',
            boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
          },
        ],
        {
          duration: 850,
          easing: 'ease-out',
        },
      );
    }
  };

  const scrollToElement = (element) => {
    const currentScrollPosition = window.scrollY ?? window.pageYOffset ?? 0;
    const targetTop = element.getBoundingClientRect().top + currentScrollPosition - NAVIGATOR_SCROLL_OFFSET;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth',
    });

    window.setTimeout(() => {
      highlightScrolledElement(element);
    }, NAVIGATOR_HIGHLIGHT_DELAY);
  };

  const scrollTo = (target) => {
    if (target === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (target === 'bottom') {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } else {
      setActiveSublocationId(String(target));
      setActiveAssetKey(null);
      const element = document.getElementById(`sublocation-${target}`);
      if (element) {
        scrollToElement(element);
      }
    }
  };

  const scrollToAsset = (sublocationId, assetId) => {
    // Queue navigation if still loading
    if (isLoading) {
      setQueuedNavigation({ type: 'asset', sublocationId, assetId });
      return;
    }

    setExpandedSublocations((prev) => ({
      ...prev,
      [sublocationId]: true,
    }));
    setActiveSublocationId(String(sublocationId));
    setActiveAssetKey(getAssetNavKey(sublocationId, assetId));

    const assetElement = document.getElementById(`asset-${sublocationId}-${assetId}`);
    if (assetElement) {
      scrollToElement(assetElement);
      return;
    }

    const sublocationElement = document.getElementById(`sublocation-${sublocationId}`);
    if (sublocationElement) {
      scrollToElement(sublocationElement);
    }
  };

  const scrollToSummary = (sublocationId) => {
    // Queue navigation if still loading
    if (isLoading) {
      setQueuedNavigation({ type: 'summary', sublocationId });
      return;
    }

    setActiveSublocationId(String(sublocationId));
    setActiveAssetKey(null);

    const summaryElement = document.getElementById(`sublocation-summary-${sublocationId}`);
    if (summaryElement) {
      scrollToElement(summaryElement);
      return;
    }

    const sublocationElement = document.getElementById(`sublocation-${sublocationId}`);
    if (sublocationElement) {
      scrollToElement(sublocationElement);
    }
  };

  const getAssetLabel = (asset) => asset?.title || asset?.name || asset?.legacyAssetId || `Asset #${asset?.id}`;

  if (!sublocations.length) return null;

  return (
    <>
      <Fab
        data-testid="sublocation-navigator-fab"
        color="primary"
        size="medium"
        className={classes.sublocationNavigatorFab}
        onClick={handleClick}
        onBlur={handleBlur}
        disabled={isLoading}
        title={isLoading ? 'Loading sublocations and assets...' : 'Navigate to sublocation'}
      >
        <Badge color="error" variant="dot" invisible={!hasAnyWarning || isLoading} overlap="circular">
          <ListAlt />
        </Badge>
      </Fab>
      <Popover
        action={popoverActionRef}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        disableAutoFocus
        disableEnforceFocus
        hideBackdrop
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
        }}
        PaperProps={{
          ref: popoverPaperRef,
          className: classes.sublocationNavigatorPopover,
          style: {
            pointerEvents: 'auto',
            maxHeight: '50vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          },
          onBlurCapture: handleBlur,
          onWheelCapture: handleNavigatorWheel,
        }}
      >
        <List dense disablePadding>
          <ListItemButton
            data-testid="sublocation-nav-top"
            className={`${classes.sublocationNavAction} ${classes.sublocationNavStickyTop}`}
            onClick={() => scrollTo('top')}
          >
            <ListItemIcon className={classes.sublocationNavIcon}>
              <ArrowUpward fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Go to top" />
          </ListItemButton>
          <Divider />
          {sublocations.map((sublocation) => {
            const isExpanded = Boolean(expandedSublocations[sublocation.id]);
            const sublocationAssets = sublocation.assets || [];
            const hasNestedItems = true;

            return (
              <React.Fragment key={sublocation.id}>
                <ListItemButton
                  data-testid={`sublocation-nav-item-${sublocation.id}`}
                  className={`${classes.sublocationNavItem} ${classes.sublocationNavSection} ${
                    String(activeSublocationId) === String(sublocation.id) ? classes.sublocationNavActive : ''
                  }`}
                  ref={(element) => {
                    if (element) {
                      sublocationItemRefs.current[sublocation.id] = element;
                      return;
                    }

                    delete sublocationItemRefs.current[sublocation.id];
                  }}
                  onClick={() => scrollTo(sublocation.id)}
                >
                  <ListItemText primary={sublocation.name} />
                  <Box display="flex" alignItems="center">
                    {sublocationWarnings[sublocation.id] && (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={MESSAGE.VARIANCE_FOUND_TOOLTIP}
                        onClick={stopIndicatorInteraction}
                        onMouseDown={stopIndicatorInteraction}
                        style={{
                          display: 'inline-flex',
                          padding: 0,
                          border: 0,
                          background: 'transparent',
                          cursor: 'default',
                        }}
                      >
                        <Tooltip
                          title={MESSAGE.VARIANCE_FOUND_TOOLTIP}
                          placement="top-end"
                          enterDelay={50}
                          leaveDelay={150}
                          enterNextDelay={0}
                          disableInteractive
                          componentsProps={{
                            popper: {
                              sx: {
                                '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                                  marginBottom: '2px !important',
                                },
                              },
                            },
                            tooltip: {
                              sx: {
                                bgcolor: '#fff',
                                color: '#111827',
                                border: '1px solid #b9c7d8',
                                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                              },
                            },
                          }}
                        >
                          <Warning
                            data-testid={`sublocation-nav-warning-icon-${sublocation.id}`}
                            color="error"
                            fontSize="small"
                            className={classes.sublocationNavWarningIcon}
                          />
                        </Tooltip>
                      </button>
                    )}
                    {hasNestedItems && (
                      <IconButton
                        size="small"
                        className={classes.sublocationNavExpandBtn}
                        data-testid={`sublocation-nav-expand-${sublocation.id}`}
                        onClick={(event) => toggleAssets(sublocation.id, event)}
                      >
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>
                </ListItemButton>
                {hasNestedItems && (
                  <Collapse
                    in={isExpanded}
                    timeout="auto"
                    unmountOnExit
                    onEntered={updatePopoverPosition}
                    onExited={updatePopoverPosition}
                  >
                    <List disablePadding dense>
                      {sublocationAssets.map((asset) => {
                        const assetNavKey = getAssetNavKey(sublocation.id, asset.id);
                        const isAssetActive =
                          activeAssetKey === assetNavKey && String(activeSublocationId) === String(sublocation.id);

                        return (
                          <ListItemButton
                            key={asset.id}
                            data-testid={`sublocation-nav-asset-${sublocation.id}-${asset.id}`}
                            className={`${classes.sublocationNavAsset} ${
                              isAssetActive ? classes.sublocationNavAssetActive : ''
                            }`}
                            aria-current={isAssetActive ? 'true' : undefined}
                            ref={(element) => {
                              if (element) {
                                assetItemRefs.current[assetNavKey] = element;
                                return;
                              }

                              delete assetItemRefs.current[assetNavKey];
                            }}
                            onClick={() => scrollToAsset(sublocation.id, asset.id)}
                          >
                            <ListItemIcon className={classes.sublocationNavIcon}>
                              <ListAlt fontSize="inherit" />
                            </ListItemIcon>
                            <ListItemText
                              primary={getAssetLabel(asset)}
                              secondary={asset?.id ? `#${asset.id}` : null}
                              className={classes.sublocationNavAssetText}
                            />
                            {assetWarnings?.[sublocation.id]?.[asset.id] ? (
                              <button
                                type="button"
                                tabIndex={-1}
                                aria-label={MESSAGE.VARIANCE_FOUND_TOOLTIP}
                                onClick={stopIndicatorInteraction}
                                onMouseDown={stopIndicatorInteraction}
                                style={{
                                  display: 'inline-flex',
                                  padding: 0,
                                  border: 0,
                                  background: 'transparent',
                                  cursor: 'default',
                                }}
                              >
                                <Tooltip
                                  title={MESSAGE.VARIANCE_FOUND_TOOLTIP}
                                  placement="top-end"
                                  enterDelay={50}
                                  leaveDelay={150}
                                  enterNextDelay={0}
                                  disableInteractive
                                  componentsProps={{
                                    popper: {
                                      sx: {
                                        '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                                          marginBottom: '2px !important',
                                        },
                                      },
                                    },
                                    tooltip: {
                                      sx: {
                                        bgcolor: '#fff',
                                        color: '#111827',
                                        border: '1px solid #b9c7d8',
                                        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                      },
                                    },
                                  }}
                                >
                                  <Warning
                                    data-testid={`sublocation-nav-asset-warning-icon-${sublocation.id}-${asset.id}`}
                                    color="error"
                                    fontSize="small"
                                    className={classes.sublocationNavWarningIcon}
                                  />
                                </Tooltip>
                              </button>
                            ) : null}
                          </ListItemButton>
                        );
                      })}
                      <ListItemButton
                        data-testid={`sublocation-nav-summary-${sublocation.id}`}
                        className={classes.sublocationNavAsset}
                        onClick={() => scrollToSummary(sublocation.id)}
                      >
                        <ListItemText primary="Summary" className={classes.sublocationNavAssetText} />
                      </ListItemButton>
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
          <Divider />
          <ListItemButton
            data-testid="sublocation-nav-bottom"
            className={`${classes.sublocationNavAction} ${classes.sublocationNavStickyBottom}`}
            onClick={() => scrollTo('bottom')}
          >
            <ListItemIcon className={classes.sublocationNavIcon}>
              <ArrowDownward fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Summary" />
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};

export default SublocationNavigator;
